import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import API_BASE_URL from "../../../config/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import axios from "axios";
import { FaFolderOpen, FaCheckCircle, FaClock, FaExclamationCircle } from "react-icons/fa";

function AdminHomeContainer() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [name, setName] = useState("");
    const [announcements, setAnnouncements] = useState([]);
    const [recentProjects, setRecentProjects] = useState([]);
    const [stats, setStats] = useState({
        totalProjects: 0,
        activeProjects: 0,
        pendingApprovals: 0,
    });

    // Update to use React Router navigation
    const gotoReport = () => {
        navigate("/admin/report");
    };

    const gotoAnnouncement = () => {
        navigate("/admin/announcements");
    };

    const gotoDefaulterList = () => {
        navigate("/admin/defaulter-list");
    };

    useEffect(() => {
        setName(user?.name || "Admin");

        const fetchData = async () => {
            try {
                await Promise.all([
                    fetchAnnouncements(),
                    fetchRecentProjects(),
                ]);
            } catch (error) {
                console.error("Error fetching dashboard data:", error);
            }
        };

        fetchData();
        AOS.init({ duration: 1500 });
    }, [user]);

    const fetchAnnouncements = async () => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/projects/announcements/general`,
                {
                    headers: {
                        Authorization: `Bearer ${user.token}`,
                    },
                }
            );
            const limitdata = response.data.slice(0, 2).sort((a, b) => new Date(b.date) - new Date(a.date));
            setAnnouncements(limitdata);
        } catch (error) {
            console.error("Error fetching announcements:", error);
            console.error("Response data:", error.response?.data);
            console.error("Status:", error.response?.status);
        }
    };

    const fetchRecentProjects = async () => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/projects/all?limit=3`,
                {
                    headers: {
                        Authorization: `Bearer ${user?.token}`,
                    },
                }
            );

            const data = response.data.projects || [];

            const active = data.filter(
                (project) =>
                    project.status === "approved" ||
                    project.status === "guide-assigned"
            ).length;

            const pending = data.filter(
                (project) =>
                    project.status === "rejected" ||
                    project.status === "pending"
            ).length;

            setStats({
                totalProjects: response.data.totalProjects || data.length,
                activeProjects: active,
                pendingApprovals: pending,
            });

            const sortedProjects = data
                .sort(
                    (a, b) =>
                        new Date(b.createdAt || b.date) -
                        new Date(a.createdAt || a.date)
                )
                .slice(0, 5);

            setRecentProjects(sortedProjects);
        } catch (error) {
            console.error("Error fetching recent projects:", error);
            console.error("Response data:", error.response?.data);
            console.error("Status:", error.response?.status);
        }
    };

    return (
        <div className="p-6 max-w-[1400px] mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center mb-8 bg-[#3f51b5] rounded-xl p-6 md:p-8 text-white shadow-md">
                <div className="flex-1 mb-4 md:mb-0 w-full md:w-auto" data-aos="fade-right">
                    <h1 className="text-3xl font-bold mb-2">Admin Dashboard</h1>
                    <p className="text-lg mb-1 opacity-90">Welcome back, {name}</p>
                    <p className="text-sm opacity-70">
                        {new Date().toLocaleDateString("en-IN", {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                        })}
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto justify-end" data-aos="fade-left">
                    <button
                        className="px-5 py-3 rounded-lg border-0 font-semibold text-sm cursor-pointer flex items-center justify-center gap-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg bg-white text-[#3f51b5]"
                        onClick={gotoAnnouncement}
                    >
                        <i className="fa fa-bullhorn"></i> Make Announcement
                    </button>
                    <button
                        className="px-5 py-3 rounded-lg border-0 font-semibold text-sm cursor-pointer flex items-center justify-center gap-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg bg-[#e91e63] text-white"
                        onClick={gotoReport}
                    >
                        <i className="fa fa-file-text"></i> Generate Report
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8" data-aos="fade-up">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 transition-transform hover:-translate-y-1 hover:shadow-md">
                    <div className="w-14 h-14 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-2xl">
                        <FaFolderOpen />
                    </div>
                    <div>
                        <h3 className="text-3xl font-bold mb-1 text-[#212121]">{stats.totalProjects}</h3>
                        <p className="text-[#757575] text-sm m-0">Total Projects</p>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 transition-transform hover:-translate-y-1 hover:shadow-md">
                    <div className="w-14 h-14 rounded-full bg-green-50 text-green-600 flex items-center justify-center text-2xl">
                        <FaCheckCircle />
                    </div>
                    <div>
                        <h3 className="text-3xl font-bold mb-1 text-[#212121]">{stats.activeProjects}</h3>
                        <p className="text-[#757575] text-sm m-0">Active Projects</p>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 transition-transform hover:-translate-y-1 hover:shadow-md">
                    <div className="w-14 h-14 rounded-full bg-yellow-50 text-yellow-600 flex items-center justify-center text-2xl">
                        <FaClock />
                    </div>
                    <div>
                        <h3 className="text-3xl font-bold mb-1 text-[#212121]">{stats.pendingApprovals}</h3>
                        <p className="text-[#757575] text-sm m-0">Pending Approvals</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
                <div className="bg-white rounded-xl p-6 shadow-md">
                    <h2 className="text-xl font-semibold mb-5 text-[#212121]">Recent Announcements</h2>
                    <div>
                        {announcements.length > 0 ? (
                            announcements.map((announcement, index) => (
                                <div key={index} className="bg-[#f5f5f7] rounded-lg p-4 mb-4 transition-all duration-300 hover:shadow-sm">
                                    <h3 className="text-base font-semibold mb-2 text-[#3f51b5]">
                                        {announcement.title}
                                    </h3>
                                    <p className="text-sm mb-2 text-[#212121]">
                                        {announcement.message.slice(0, 50) + (announcement.message.length > 50 ? "..." : "")}
                                    </p>
                                    <p className="text-xs m-0 text-[#757575] text-right">
                                        {new Date(
                                            announcement.date
                                        ).toLocaleDateString("en-IN", {
                                            day: "numeric",
                                            month: "short",
                                            year: "numeric",
                                        })}
                                    </p>
                                </div>
                            ))
                        ) : (
                            <div className="text-[#757575] italic text-center p-6">
                                <p>No announcements available</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md h-fit">
                    <h2 className="text-xl font-semibold mb-5 text-[#212121]">Recent Activity</h2>
                    <div className="relative before:content-[''] before:absolute before:left-3 before:top-0 before:h-full before:w-[2px] before:bg-[#f5f5f7]">
                        {recentProjects.length > 0 ? (
                            recentProjects.map((project, index) => (
                                <div key={index} className="flex mb-5 relative">
                                    <span className="w-3 h-3 rounded-full bg-[#3f51b5] mt-1.5 mr-4 ml-1.5 z-10 shrink-0"></span>
                                    <div className="flex-1">
                                        <p className="text-sm mb-1 text-[#212121]">
                                            New project "
                                            {project.projectTitle ||
                                                project.title}
                                            " added
                                        </p>
                                        <p className="text-xs m-0 text-[#757575]">
                                            {new Date(
                                                project.createdAt ||
                                                project.date
                                            ).toLocaleDateString(
                                                "en-IN", {
                                                day: "numeric",
                                                month: "short",
                                                year: "numeric",
                                            }
                                            )}
                                        </p>
                                        <p className="text-xs m-0 text-[#3f51b5] font-medium">
                                            {project.department &&
                                                `Department: ${project.department}`}
                                        </p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-[#757575] italic text-center p-6">
                                <p>No recent project activity</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Analytics Card */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border-l-4 border-[#B7202E]">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 mb-2 flex items-center gap-2">
                                <FaExclamationCircle className="text-orange-500" /> Students without Project Submissions
                            </h3>
                            <p className="text-gray-600 text-sm">
                                View a distinct list of students who have not yet submitted or joined any project group.
                            </p>
                        </div>
                        <button
                            className="bg-white border border-[#B7202E] text-[#B7202E] px-6 py-2.5 rounded-lg font-bold hover:bg-[#B7202E] hover:text-white transition-all text-sm whitespace-nowrap shadow-sm"
                            onClick={gotoDefaulterList}
                        >
                            View Details
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AdminHomeContainer;
